import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';

import {
  gray100,
  gray40,
  gray80,
  orange50,
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  white,
} from '../../../../constants/colors';
import { boldMd } from '../../../../constants/location';
import { ocrCreditAddSchema } from '../../../../validation/ocrCredit';
import type { OcrCreditAddFormType } from '../../../../types/ocrCreditUsage';
import type { CompanyAccountType } from '../../../../types/company';

import CustomTextfield from '../../../uiParts/form/CustomTextfield';
import CustomSnackbar from '../../../uiParts/form/CustomSnackbar';
import SideBar from '../../../uiParts/menu/SideBar';
import CustomLoading from '../../../uiParts/form/CustomLoading';
import OcrCreditAddConfirmModal from '../../../uiParts/modal/OcrCreditAddConfirmModal';
import MetaTitle from '../../../utils/MetaTitle';
import { useAdminPageAccessControl } from '../../../../hooks/useAdminPageAccessControl';
import { getCompanyForAccount } from '../../../../api/firebase/firestore/company/read';
import { addOcrCredit } from '../../../../api/firebase/firestore/ocrCreditUsage/create';
import {
  OCR_CREDIT_ADD_TITLE,
  OCR_CREDIT_ADD_CREDIT_LABEL,
  OCR_CREDIT_ADD_NOTE_LABEL,
  OCR_CREDIT_ADD_BACK_BUTTON,
  OCR_CREDIT_ADD_CONFIRM_BUTTON,
  OCR_CREDIT_ADD_ERROR_UPDATE,
  OCR_CREDIT_EXPIRES_AT_LABEL,
  OCR_CREDIT_EXPIRES_AT_NOTE,
  REQUIRED_LABEL,
} from '../../../../constants/labels';
import { AdminNoPermissionPage } from '../../../uiParts/parts/AdminNoPermission';
import { usePageLayout } from '../../../../hooks/usePageLayout';

const styles = {
  formContainer: {
    alignItems: 'center',
    backgroundColor: white,
    borderRadius: '4px',
    border: `1px solid ${gray40}`,
    flexDirection: 'column',
    p: '39px',
    width: '480px',
    boxSizing: 'border-box',
    '@media screen and (max-width: 960px)': {
      // 左右の余白は親（pageContentSx）の padding で確保する。
      // width:100% にマージンを足すと親幅を超えて余白として現れないため。
      width: '100%',
      maxWidth: '480px',
      boxSizing: 'border-box',
      p: '32px 16px',
    },
  },
  title: {
    '@media screen and (max-width:960px)': {
      fontSize: '20px',
    },
    color: gray100,
    fontSize: '22px',
    fontWeight: '500',
    mb: '24px',
    textAlign: 'left',
  },
  buttonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '8px',
    width: '100%',
    '@media screen and (max-width:960px)': {
      gap: '8px',
    },
  },
  backButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  backButtonText: {
    ...boldMd,
    color: gray80,
  },
  confirmButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  confirmButtonText: {
    ...boldMd,
    color: 'white',
  },
  creditInputWrapper: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
    marginBottom: '24px',
  },
  unitText: {
    color: gray100,
    fontSize: '16px',
    marginBottom: '14px',
  },
  expiresAtWrapper: {
    marginBottom: '24px',
  },
  expiresAtRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  expiresAtLabel: {
    color: gray100,
    fontSize: '14px',
    fontWeight: '500',
  },
  expiresAtValue: {
    color: orange50,
    fontSize: '14px',
    fontWeight: '500',
    marginLeft: '8px',
  },
  expiresAtNote: {
    color: gray80,
    fontSize: '12px',
    marginTop: '4px',
    textAlign: 'center',
  },
  errorContainer: {
    alignItems: 'center',
    backgroundColor: white,
    borderRadius: '4px',
    border: `1px solid ${gray40}`,
    flexDirection: 'column',
    p: '39px',
    textAlign: 'center',
    '@media screen and (max-width: 960px)': {
      // 左右の余白は親（pageContentSx）の padding で確保する
      width: '100%',
      maxWidth: '480px',
      boxSizing: 'border-box',
      p: '32px 16px',
    },
  },
  errorText: {
    color: gray100,
    fontSize: '16px',
    marginBottom: '24px',
  },
};

const OcrCreditAdd: React.FC = (): JSX.Element => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { user, noPermission, isChecking } = useAdminPageAccessControl('OcrCreditAdd');
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();

  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [companyData, setCompanyData] = useState<CompanyAccountType | null>(null);
  const [errorText, setErrorText] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [companyNotFound, setCompanyNotFound] = useState<boolean>(false);

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<OcrCreditAddFormType>({
    resolver: yupResolver(ocrCreditAddSchema),
    defaultValues: {
      ocrCredit: undefined,
      note: '',
    },
  });

  const ocrCreditValue = watch('ocrCredit');

  // 有効期限を計算（付与日から1年後）
  const calculateExpiresAt = (): Date => {
    const expiresAt = new Date();
    expiresAt.setFullYear(expiresAt.getFullYear() + 1);
    return expiresAt;
  };

  const expiresAt = calculateExpiresAt();

  // 有効期限の表示フォーマット
  const formatExpiresAt = (date: Date): string => {
    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
  };

  // 確認ボタンの活性/非活性
  const isConfirmButtonDisabled = (): boolean => {
    const credit = ocrCreditValue;
    if (credit === undefined || credit === null) return true;
    if (typeof credit === 'number' && credit >= 1) return false;
    return true;
  };

  // 企業情報を取得（アクセス制御はフックに委譲）
  useEffect(() => {
    if (isChecking || noPermission || !user) return;

    const fetchCompany = async (): Promise<void> => {
      if (!companyId) {
        setCompanyNotFound(true);
        setLoading(false);
        return;
      }

      try {
        const company = await getCompanyForAccount(companyId);
        if (!company) {
          Sentry.captureMessage('Company not found in OcrCreditAdd', {
            level: 'warning',
            extra: { companyId },
          });
          setCompanyNotFound(true);
        } else {
          setCompanyData(company);
        }
      } catch (error) {
        Sentry.captureException(error);
        setCompanyNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, [isChecking, noPermission, user, companyId]);

  // 戻るボタン
  const handleBack = (): void => {
    navigate(-1);
  };

  // 確認ボタン
  const handleConfirmClick = (): void => {
    handleSubmit(() => {
      setConfirmDialogOpen(true);
    })();
  };

  // 確認ダイアログで追加実行
  const handleAddCredit = async (): Promise<void> => {
    if (!companyId || !user || !companyData) return;

    setSubmitting(true);
    try {
      const amount = watch('ocrCredit');
      const note = watch('note');

      await addOcrCredit(companyId, amount, note, user.uid);

      setConfirmDialogOpen(false);
      navigate(`/admin/ocr_credit_add/${companyId}/complete`, {
        state: { fromOcrCreditAdd: true },
      });
    } catch (error) {
      Sentry.captureException(error);
      setErrorText(OCR_CREDIT_ADD_ERROR_UPDATE);
      setSnackbarOpen(true);
      setConfirmDialogOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  // 権限エラー表示（運営オペレーター向け）
  if (noPermission) {
    return <AdminNoPermissionPage companyId={companyId} customTitle={OCR_CREDIT_ADD_TITLE} />;
  }

  // ローディング中はフォームを描画しない
  if (isChecking || loading) {
    return (
      <Box sx={layoutStyles.pageWrapper}>
        <MetaTitle customTitle={OCR_CREDIT_ADD_TITLE} />
        <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
          <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
        </Box>
        <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
          <CustomLoading loading={true} />
        </Box>
      </Box>
    );
  }

  const pageContentSx = [
    layoutStyles.pageContent,
    sideBarCollapsed && layoutStyles.pageContentCollapsed,
    layoutStyles.pageContentNoFooter,
    layoutStyles.fullHeightContent,
    {
      justifyContent: 'center',
      alignItems: 'center',
      // カード左右の余白はここで確保する（子側でマージンを足すと親幅を超えるため）。
      // 一括指定にすると共通レイアウトの paddingBottom を意図せず打ち消すため、個別に指定する。
      paddingTop: '16px',
      paddingLeft: '16px',
      paddingRight: '16px',
      boxSizing: 'border-box',
    },
  ];

  // 企業が存在しない場合
  if (companyNotFound) {
    return (
      <Box sx={layoutStyles.pageWrapper}>
        <MetaTitle customTitle={OCR_CREDIT_ADD_TITLE} />
        <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
          <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
        </Box>
        <Box sx={pageContentSx}>
          <Box sx={styles.errorContainer}>
            <Typography sx={styles.errorText}>指定された企業が見つかりませんでした。</Typography>
            <Button
              onClick={(): void => navigate('/admin/company')}
              sx={[primaryButtonBackgroundColor, { width: '200px', height: '44px' }]}
              variant="contained"
            >
              企業一覧に戻る
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle customTitle={OCR_CREDIT_ADD_TITLE} />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>
      <Box sx={pageContentSx}>
        <Typography component="div" sx={styles.formContainer}>
          <Typography sx={styles.title}>{OCR_CREDIT_ADD_TITLE}</Typography>

          <Box sx={styles.creditInputWrapper}>
            <Controller
              render={({ field }): JSX.Element => (
                <CustomTextfield
                  {...field}
                  type="text"
                  inputMode="numeric"
                  error={Boolean(errors.ocrCredit)}
                  helperText={errors.ocrCredit?.message}
                  labelText={OCR_CREDIT_ADD_CREDIT_LABEL}
                  requiredText={REQUIRED_LABEL}
                  inputWidth="200px"
                  formMb="0px"
                  sx={{ '& input': { textAlign: 'right' } }}
                  onChange={(e): void => {
                    const value = e.target.value;
                    // 数字のみ許可
                    if (value === '' || /^\d+$/.test(value)) {
                      field.onChange(value === '' ? undefined : Number(value));
                    }
                  }}
                  value={field.value ?? ''}
                />
              )}
              control={control}
              name="ocrCredit"
            />
            <Typography sx={styles.unitText}>円</Typography>
          </Box>

          <Controller
            render={({ field }): JSX.Element => (
              <CustomTextfield
                {...field}
                multiline
                rows={4}
                error={Boolean(errors.note)}
                helperText={errors.note?.message}
                labelText={OCR_CREDIT_ADD_NOTE_LABEL}
                requiredText={REQUIRED_LABEL}
                inputWidth="100%"
                height="120px"
                formMb="24px"
              />
            )}
            control={control}
            name="note"
          />

          <Box sx={styles.expiresAtWrapper}>
            <Box sx={styles.expiresAtRow}>
              <Typography sx={styles.expiresAtLabel}>{OCR_CREDIT_EXPIRES_AT_LABEL}:</Typography>
              <Typography sx={styles.expiresAtValue}>{formatExpiresAt(expiresAt)}</Typography>
            </Box>
            <Typography sx={styles.expiresAtNote}>{OCR_CREDIT_EXPIRES_AT_NOTE}</Typography>
          </Box>

          <Box sx={styles.buttonWrapper}>
            <Button sx={styles.backButton} onClick={handleBack}>
              <Typography sx={styles.backButtonText}>{OCR_CREDIT_ADD_BACK_BUTTON}</Typography>
            </Button>
            <Button
              sx={styles.confirmButton}
              onClick={handleConfirmClick}
              disabled={isConfirmButtonDisabled()}
            >
              <Typography sx={styles.confirmButtonText}>{OCR_CREDIT_ADD_CONFIRM_BUTTON}</Typography>
            </Button>
          </Box>
        </Typography>
      </Box>

      <OcrCreditAddConfirmModal
        open={confirmDialogOpen}
        onClose={(): void => setConfirmDialogOpen(false)}
        onConfirm={handleAddCredit}
        amount={ocrCreditValue ?? 0}
        expiresAt={expiresAt}
        loading={submitting}
      />

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={errorText}
      />
    </Box>
  );
};

export default OcrCreditAdd;
